import axios from 'axios';
import ApiEndpoints from '../const/ApiEndpoints';
import SwalToast from '../components/Common/SwalToast';

export default function useUserGroupPermission() {
  /**
   * @function fetchUserDetails
   * @description Fetches user details based on the provided organization ID.
   * @param {object} body request body containing the organization ID.
   * @returns {promise} axios promise that resolves with the user details data.
   * @example fetchUserDetails(body)
   */
  const fetchUserDetails = (body, id) =>
    axios
      .get(`${ApiEndpoints.BASE_URL + ApiEndpoints.GET_USER_DETAILS}`, {
        params: body,
        headers: {
          'X-Tenant-ID': id,
        },
      })
      .then((res) => {
        return res?.data;
      });

  /**
   * @function createUser
   * @description Creates a new user
   * @param {object} payload The data payload for creating a new user.
   * @returns {promise} promise that resolves with creating a new user
   * @example createUser(payload)
   */
  const createUser = (payload, id) =>
    axios
      .post(
        `${ApiEndpoints.BASE_URL + ApiEndpoints.CREATE_USER_DETAILS}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': id,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'User created successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function deteleUser
   * @description Deletes a user
   * @param {number} organizationID ID of the organization to which the user belongs.
   * @param {string} id ID of the user to be deleted.
   * @returns {promise} promise that resolves with deleting the user
   * @example deteleUser(organizationID, id)
   */
  const deteleUser = (organizationID, id) =>
    axios
      .delete(
        `${ApiEndpoints.BASE_URL}${ApiEndpoints.DELETE_USER_DETAILS}${id}/`,
        {
          headers: {
            'X-Tenant-ID': organizationID,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'User deleted successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function updateUser
   * @description Updates the user details
   * @param {number} organizationID The ID of the organization to which the user belongs.
   * @param {string} id The ID of the user to be updated.
   * @returns {promise} promise that resolves with updating user details
   * @example updateUser(organizationID, id, payload)
   */
  const updateUser = (organizationID, id, payload) =>
    axios
      .put(
        `${ApiEndpoints.BASE_URL}${ApiEndpoints.UPDATE_USER_DETAILS}${id}/`,
        payload,
        {
          headers: {
            'X-Tenant-ID': organizationID,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'User updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @description this function fetches a list of groups.
   * @param {object} payload The request payload containing optional parameters.
   * @returns {promise} this promise resolves with the list of groups.
   * @example fetchGroupList(payload)
   */
  const fetchGroupList = (payload) =>
    axios
      .get(`${ApiEndpoints.BASE_URL}${ApiEndpoints.GET_GROUP_LIST}`, {
        params: {
          group_id: payload.groupID,
          user_details: payload.userDetails,
          organization: payload.organization,
        },
      })
      .then((res) => {
        return res;
      });

  /**
   * @description this function updates the user actions.
   * @param {object} payload the data payload for updating user actions.
   * @param {string} id the ID of the user.
   * @returns {promise} promise that resolves with updating useractions
   * @example updateUserActions(payload,id)
   */
  const updateUserActions = (payload, id) =>
    axios
      .post(`${ApiEndpoints.BASE_URL}/users/bulk-action/`, payload, {
        headers: {
          'X-Tenant-ID': id,
        },
      })
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'User action updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @description updates the password of a user on the server.
   * @param {object} payload the data payload for updating the user password.
   * @param {number} org_id ID of organization
   * @param {string} user_id ID of the user.
   * @returns {promise} promise that resolves with updating the user password.
   * @example updateUserPassword(payload, org_id, user_id)
   */
  const updateUserPassword = (payload, orgId, userId) =>
    axios
      .patch(
        `${ApiEndpoints.BASE_URL}${ApiEndpoints.RESET_USER_PASSWORD}/${orgId}/${userId}`,
        payload
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title:
              payload.action === 'RESET_PASSWORD_VIA_EMAIL_LINK'
                ? 'Reset link has been sent your registered Email.'
                : 'User password updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @description updates the permission of user group
   * @param {object} payload data payload for updating the user group's permission.
   * @param {string} user_id The ID of the user.
   * @returns {promise} A Promise that resolves with updating user permission
   * @example updateGroupPermission(payload,user_id)
   */
  const updateGroupPermission = (payload, userId, orgId) =>
    axios
      .put(
        `${ApiEndpoints.BASE_URL}${ApiEndpoints.UPDATE_PERMISSION}${userId}/`,
        payload,
        {
          headers: {
            'X-Tenant-ID': orgId,
          },
        }
      )
      .then(
        (res) => {
          SwalToast({
            icon: 'success',
            title: 'User permission updated successfully.',
          });
          return res;
        },
        (error) => {
          throw error;
        }
      );

  return {
    fetchUserDetails,
    createUser,
    fetchGroupList,
    updateUserActions,
    updateUserPassword,
    updateGroupPermission,
    deteleUser,
    updateUser,
  };
}
